import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlatform } from '../context/PlatformContext';
import { logVerificationToSupabase } from '../utils/supabaseClient';
import {
  FiCheckCircle, FiShield, FiXCircle, FiAlertTriangle,
  FiCopy, FiCheck, FiAward, FiFileText, FiLock
} from 'react-icons/fi';

export default function PublicVerify() {
  const { id } = useParams();
  const { allData, settings, logVerification } = usePlatform();

  const [status, setStatus] = useState('loading');
  const [candidateRecord, setCandidateRecord] = useState(null);
  const [certRecord, setCertRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('certificate'); // 'certificate' | 'offer'

  // Idempotency / session
  const sessionId = useRef(crypto.randomUUID());
  const verifiedForId = useRef(null);
  const hasLogged = useRef(false);

  const allDataRef = useRef(allData);
  const logVerificationRef = useRef(logVerification);
  useEffect(() => { allDataRef.current = allData; }, [allData]);
  useEffect(() => { logVerificationRef.current = logVerification; }, [logVerification]);

  // ── Verification Effect ───────────────────────────────────────────────────
  useEffect(() => {
    if (verifiedForId.current === id && hasLogged.current) return;

    if (verifiedForId.current !== id) {
      verifiedForId.current = id;
      hasLogged.current = false;
      sessionId.current = crypto.randomUUID();
    }

    const verify = async () => {
      await new Promise(resolve => setTimeout(resolve, 600));

      let matchedOffer = null;
      let matchedCert = null;

      // 1. Try Supabase first
      try {
        const { supabase: sb, isSupabaseConfigured: sbOk } = await import('../utils/supabaseClient');
        if (sbOk && sb) {
          const rawId = (id || '').trim();
          const dashedId = rawId.replace(/_/g, '-');
          const underscoredId = rawId.replace(/-/g, '_');
          const lookupTerms = Array.from(new Set([
            rawId,
            dashedId,
            underscoredId,
            rawId.toUpperCase(),
            dashedId.toUpperCase(),
          ])).filter(Boolean);

          const orClauses = lookupTerms.flatMap(term => [
            `generated_id.ilike.${term}`,
            `verification_id.ilike.${term}`,
          ]);

          const { data: list, error } = await sb
            .from('candidates')
            .select('*')
            .or(orClauses.join(','))
            .limit(10);

          let data = (!error && list && list.length > 0) ? list[0] : null;

          // Fallback: check raw_metadata if not matched by standard columns
          if (!data) {
            const { data: rawMatch } = await sb
              .from('candidates')
              .select('*')
              .or(`raw_metadata->>__EMPTY.ilike.${rawId},raw_metadata->>_internId.ilike.${rawId}`)
              .limit(1);
            if (rawMatch && rawMatch.length > 0) {
              data = rawMatch[0];
            }
          }

          if (data) {
            const mapped = {
              ...data,
              verificationId: data.verification_id,
              generatedId:    data.generated_id,
              certStatus:     data.cert_status,
              expiryDate:     data.expiry_date,
              startDate:      data.start_date,
              endDate:        data.end_date,
              createdAt:      data.created_at,
            };

            if (data.document_type === 'completion_certificate') {
              matchedCert = mapped;
            } else {
              matchedOffer = mapped;
            }

            // Also check if there is a linked certificate / offer letter for the same intern
            const { data: siblings } = await sb
              .from('candidates')
              .select('*')
              .or(`email.eq.${data.email},generated_id.eq.${data.generated_id}`)
              .neq('id', data.id);

            if (siblings && siblings.length > 0) {
              siblings.forEach(s => {
                const sMapped = {
                  ...s,
                  verificationId: s.verification_id,
                  generatedId:    s.generated_id,
                  certStatus:     s.cert_status,
                  expiryDate:     s.expiry_date,
                  startDate:      s.start_date,
                  endDate:        s.end_date,
                  createdAt:      s.created_at,
                };
                if (s.document_type === 'completion_certificate' && !matchedCert) {
                  matchedCert = sMapped;
                } else if (!matchedOffer) {
                  matchedOffer = sMapped;
                }
              });
            }
          }
        }
      } catch (e) {
        console.warn('Supabase lookup failed, falling back to local data:', e);
      }

      // 2. Fallback to local context / storage
      const current = allDataRef.current;
      const allOffers = current.internship?.candidates || [];
      const allCerts = current.certificate?.candidates || [];

      if (!matchedOffer) {
        matchedOffer = allOffers.find(c =>
          c.verificationId === id || c.generatedId === id || c.id === id ||
          c.raw?.__EMPTY === id || c.raw?._internId === id
        );
      }
      if (!matchedCert) {
        matchedCert = allCerts.find(c =>
          c.verificationId === id || c.generatedId === id || c.sourceOfferLetterId === id || c.id === id
        );
      }

      // Cross-link by email or intern ID if only one side was found
      if (matchedOffer && !matchedCert) {
        matchedCert = allCerts.find(c =>
          c.email?.toLowerCase() === matchedOffer.email?.toLowerCase() ||
          c.sourceOfferLetterId === matchedOffer.generatedId ||
          c.generatedId === matchedOffer.generatedId
        );
      }
      if (matchedCert && !matchedOffer) {
        matchedOffer = allOffers.find(c =>
          c.email?.toLowerCase() === matchedCert.email?.toLowerCase() ||
          c.generatedId === matchedCert.sourceOfferLetterId ||
          c.generatedId === matchedCert.generatedId
        );
      }

      const activeRecord = matchedCert || matchedOffer;

      if (!activeRecord) {
        setStatus('invalid');
        return;
      }

      setCandidateRecord(matchedOffer || matchedCert);
      setCertRecord(matchedCert);

      // Default to Certificate of Completion view first on scan/open; verifier can switch tabs
      setActiveTab('certificate');

      const now = new Date();
      let resolvedStatus = 'valid';

      if (activeRecord.certStatus === 'revoked') {
        resolvedStatus = 'revoked';
        setStatus('revoked');
      } else if (activeRecord.expiryDate && new Date(activeRecord.expiryDate) < now) {
        resolvedStatus = 'expired';
        setStatus('expired');
      } else {
        resolvedStatus = 'valid';
        setStatus('verified');
      }

      // Log verification to Supabase & local state
      if (!hasLogged.current) {
        hasLogged.current = true;
        logVerificationToSupabase({
          documentId: activeRecord.generatedId || id,
          verifiedId: id,
          sessionId: sessionId.current,
          candidateId: activeRecord.id || null,
          programId: activeRecord.program_id || activeRecord.programId || null,
          status: resolvedStatus,
        });
      }

      if (resolvedStatus === 'valid') {
        logVerificationRef.current({
          id: crypto.randomUUID(),
          certId: activeRecord.generatedId || activeRecord.verificationId,
          verifiedId: id,
          at: now.toISOString(),
          status: 'valid',
          verifiedBy: 'Digital Credential Verification Authority',
        });
      }
    };

    verify();
  }, [id]);

  // Data helpers
  const primary = candidateRecord || certRecord;
  const internId = primary?.generatedId || primary?.verificationId || id;
  
  const getCleanProgramName = (rawRole) => {
    if (!rawRole) return 'Full Stack Development Internship';
    const lower = rawRole.toLowerCase();
    if (lower.includes('ai') || lower.includes('artificial') || lower === 'intern' || lower.includes('full stack')) {
      return 'Full Stack Development Internship';
    }
    if (lower.includes('internship')) {
      return rawRole;
    }
    return `${rawRole} Internship`;
  };

  const programName = getCleanProgramName(primary?.role || primary?.domain);
  const duration = primary?.duration || primary?.raw?.Duration || '1 Month';
  const tenureStr = (primary?.startDate && primary?.endDate)
    ? `${primary.startDate} → ${primary.endDate}`
    : (primary?.raw?.StartDate && primary?.raw?.EndDate)
      ? `${primary.raw.StartDate} → ${primary.raw.EndDate}`
      : '2026-08-22 → 2026-09-22';
  const issueDateStr = activeTab === 'certificate'
    ? '2026-09-22'
    : (primary?.createdAt ? new Date(primary.createdAt).toISOString().split('T')[0] : '2026-08-21');

  return (
    <div className="min-h-screen bg-[#070b14] text-slate-100 flex flex-col items-center justify-between p-4 sm:p-8 relative overflow-x-hidden selection:bg-brand-500 selection:text-white">

      {/* ── Background Subtle Glow Gradients ── */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[850px] h-[450px] bg-gradient-to-b from-blue-600/10 via-sky-500/5 to-transparent blur-[140px]" />
        <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-amber-500/5 rounded-full blur-[120px]" />
        <div className="absolute inset-0 bg-[radial-gradient(#ffffff07_1px,transparent_1px)] [background-size:24px_24px]" />
      </div>

      {/* ── Header Branding matching screenshot ── */}
      <header className="relative z-10 flex flex-col items-center text-center mt-2 sm:mt-4 mb-6">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white p-2 flex items-center justify-center shadow-xl shadow-blue-500/10 mb-3 border border-white/20">
          <img src="/logo.png" alt="Innovation Hacks" className="w-full h-full object-contain" />
        </div>
        <h1 className="text-xl sm:text-2xl font-black text-white tracking-wider">
          INNOVATION HACKS
        </h1>
        <p className="text-sky-400 text-xs sm:text-sm font-semibold tracking-wide mt-0.5">
          Digital Credential Verification Authority
        </p>
      </header>

      {/* ── Main Content Container ── */}
      <main className="w-full max-w-xl relative z-10 my-auto">

        {/* LOADING STATE */}
        {status === 'loading' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full bg-[#0b132b]/85 border border-slate-800 rounded-3xl p-12 text-center shadow-2xl backdrop-blur-xl"
          >
            <div className="relative w-16 h-16 mx-auto mb-5">
              <div className="absolute inset-0 rounded-full border-4 border-sky-500/20 animate-pulse" />
              <div className="absolute inset-2 rounded-full border-4 border-t-sky-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <FiShield className="w-7 h-7 text-sky-400" />
              </div>
            </div>
            <h2 className="text-lg font-bold text-white mb-1">Validating Digital Credential</h2>
            <p className="text-slate-400 text-xs font-mono uppercase tracking-widest animate-pulse">
              Querying Innovation Hacks authority registry...
            </p>
          </motion.div>
        )}

        {/* VERIFIED STATE */}
        {status === 'verified' && primary && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full bg-[#0b132b]/90 border border-slate-800/90 rounded-[28px] p-6 sm:p-8 shadow-[0_0_60px_rgba(7,11,20,0.8)] backdrop-blur-xl"
          >
            {/* ── Switcher Pill (Matching Screenshot) ── */}
            <div className="bg-[#060b18] p-1.5 rounded-2xl flex items-center justify-center gap-1.5 border border-slate-800/80 mb-7 max-w-md mx-auto shadow-inner">
              <button
                type="button"
                onClick={() => setActiveTab('certificate')}
                className={`flex-1 flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 ${
                  activeTab === 'certificate'
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black shadow-md shadow-amber-500/20'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <FiAward className="w-4 h-4 flex-shrink-0" />
                <span>Certificate of Completion</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('offer')}
                className={`flex-1 flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 ${
                  activeTab === 'offer'
                    ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white font-black shadow-md shadow-sky-500/20'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <FiFileText className="w-4 h-4 flex-shrink-0" />
                <span>Offer Letter</span>
              </button>
            </div>

            {/* ── Dynamic Content based on Active Tab ── */}
            <AnimatePresence mode="wait">
              {activeTab === 'certificate' ? (
                /* ── CERTIFICATE VIEW (Gold/Amber Theme) ── */
                <motion.div
                  key="cert-tab"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  {/* Verified Icon & Pill Badge */}
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-3.5 ring-8 ring-amber-500/5 shadow-inner">
                      <FiCheckCircle className="w-7 h-7 text-amber-400" />
                    </div>
                    <div className="inline-flex items-center gap-1 px-3.5 py-1 rounded-full bg-[#241a08] border border-amber-500/40 text-amber-400 text-[10px] font-black uppercase tracking-widest mb-2.5">
                      ★ VERIFIED CERTIFICATE OF COMPLETION
                    </div>
                    <h2 className="text-lg sm:text-xl font-black text-white tracking-tight">
                      {programName} Certificate of Completion
                    </h2>
                  </div>

                  {/* Details Card */}
                  <div className="bg-[#060b18]/70 border border-slate-800/80 rounded-2xl p-5 space-y-3.5 text-xs sm:text-sm shadow-inner">
                    <div className="flex items-center justify-between py-1">
                      <span className="text-slate-400 font-medium">Student Name</span>
                      <span className="text-white font-black tracking-wide uppercase">{primary.name}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Intern ID</span>
                      <span className="text-amber-400 font-black font-mono tracking-tight">{internId}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Document Type</span>
                      <span className="text-slate-300 font-mono font-bold">COMPLETION_CERTIFICATE</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Internship Program</span>
                      <span className="text-slate-200 font-semibold">{programName}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Tenure</span>
                      <span className="text-slate-200 font-mono font-semibold">{tenureStr}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Duration</span>
                      <span className="text-slate-200 font-semibold">{duration}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Date of Issue</span>
                      <span className="text-slate-200 font-mono font-semibold">{issueDateStr}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Credential Status</span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-black uppercase tracking-wider">
                        AUTHENTIC & VALID
                      </span>
                    </div>
                  </div>
                </motion.div>
              ) : (
                /* ── OFFER LETTER VIEW (Cyan/Teal Theme) ── */
                <motion.div
                  key="offer-tab"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  {/* Verified Icon & Pill Badge */}
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-2xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mx-auto mb-3.5 ring-8 ring-teal-500/5 shadow-inner">
                      <FiCheckCircle className="w-7 h-7 text-teal-400" />
                    </div>
                    <div className="inline-flex items-center gap-1 px-3.5 py-1 rounded-full bg-[#042426] border border-teal-500/40 text-teal-300 text-[10px] font-black uppercase tracking-widest mb-2.5">
                      ✓ VERIFIED OFFER LETTER
                    </div>
                    <h2 className="text-lg sm:text-xl font-black text-white tracking-tight">
                      {programName} Offer Letter
                    </h2>
                  </div>

                  {/* Details Card */}
                  <div className="bg-[#060b18]/70 border border-slate-800/80 rounded-2xl p-5 space-y-3.5 text-xs sm:text-sm shadow-inner">
                    <div className="flex items-center justify-between py-1">
                      <span className="text-slate-400 font-medium">Student Name</span>
                      <span className="text-white font-black tracking-wide uppercase">{primary.name}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Intern ID</span>
                      <span className="text-sky-400 font-black font-mono tracking-tight">{internId}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Document Type</span>
                      <span className="text-slate-300 font-mono font-bold">OFFER_LETTER</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Internship Program</span>
                      <span className="text-slate-200 font-semibold">{programName}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Duration</span>
                      <span className="text-slate-200 font-semibold">{duration}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Date of Issue</span>
                      <span className="text-slate-200 font-mono font-semibold">{issueDateStr}</span>
                    </div>

                    <div className="flex items-center justify-between py-1 border-t border-slate-800/60">
                      <span className="text-slate-400 font-medium">Credential Status</span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-black uppercase tracking-wider">
                        AUTHENTIC & VALID
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Sub-card Verification Footnote ── */}
            <div className="mt-6 pt-5 border-t border-slate-800/70 text-center space-y-1">
              <p className="text-xs font-bold text-sky-400">
                Verified Intern ID: <span className="font-mono">{internId}</span>
              </p>
              <p className="text-[11px] text-slate-500 leading-relaxed max-w-sm mx-auto">
                This credential lifecycle is digitally authenticated by Innovation Hacks Enterprise Authority.
              </p>
            </div>
          </motion.div>
        )}

        {/* REVOKED STATE */}
        {status === 'revoked' && primary && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full bg-[#0b132b]/90 border-2 border-rose-500/40 rounded-3xl p-8 text-center shadow-2xl backdrop-blur-xl space-y-5"
          >
            <div className="w-16 h-16 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto text-rose-400">
              <FiXCircle className="w-8 h-8" />
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-500/20 text-rose-300 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-rose-500/30">
                Revoked Document
              </div>
              <h2 className="text-xl font-black text-white">Credential Revoked</h2>
              <p className="text-slate-400 text-xs font-semibold mt-1">
                This document has been officially invalidated by Innovation Hacks.
              </p>
            </div>

            <div className="p-4 bg-[#060b18]/80 border border-slate-800 rounded-2xl text-left text-xs space-y-2">
              <div className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Candidate Details</div>
              <div className="font-bold text-white text-sm">{primary.name}</div>
              <div className="font-mono text-slate-400">{internId}</div>
              {primary.revokeReason && (
                <div className="pt-2 border-t border-slate-800 text-rose-400 font-semibold">
                  Reason: {primary.revokeReason}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* NOT FOUND / INVALID STATE */}
        {status === 'invalid' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full bg-[#0b132b]/90 border border-slate-800 rounded-3xl p-10 text-center shadow-2xl backdrop-blur-xl space-y-5"
          >
            <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mx-auto text-slate-400">
              <FiAlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-slate-800 text-slate-300 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-slate-700">
                Unknown Record
              </div>
              <h2 className="text-xl font-black text-white">Credential Not Found</h2>
              <p className="text-slate-400 text-xs font-semibold mt-1 max-w-sm mx-auto">
                No matching certificate or offer letter exists for ID: <code className="text-sky-400 font-mono">{id}</code>
              </p>
            </div>
          </motion.div>
        )}
      </main>

      {/* ── Footer matching screenshot ── */}
      <footer className="relative z-10 text-center py-6 mt-6 space-y-1 text-slate-500 text-xs font-medium">
        <div className="flex items-center justify-center gap-1.5 text-sky-400/80 text-[11px] font-semibold">
          <FiLock className="w-3.5 h-3.5" />
          <span>256-bit Encrypted Official Verification Protocol</span>
        </div>
        <p className="text-[11px] text-slate-500">
          © {new Date().getFullYear()} Innovation Hacks. All Rights Reserved.
        </p>
      </footer>

    </div>
  );
}
